import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/client';

/**
 * useFeatures — OFManager module gating client-side.
 *
 * Loads `/api/billing/my-features` once, caches in sessionStorage with a 5-min
 * TTL, and exposes `{ enabled, has(key), catalog, plan, refresh, loading }`.
 *
 * Design notes :
 *  - We deliberately use sessionStorage (not localStorage) so a logout / tab
 *    close fully resets the cache.
 *  - The cache invalidates on `ifaap:features-changed` events — broadcast by
 *    the SuperAdmin page after a plan/tenant patch — so a superadmin demoting
 *    a tenant sees the UI update without a full reload.
 */
const CACHE_KEY = 'ifaap_features_cache_v1';
const TTL_MS = 5 * 60 * 1000;

const _readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - (parsed.ts || 0) > TTL_MS) return null;
    return parsed.data || null;
  } catch { return null; }
};
const _writeCache = (data) => {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); }
  catch { /* noop */ }
};
const _clearCache = () => {
  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
};

export function useFeatures() {
  const [state, setState] = useState(() => _readCache());
  const [loading, setLoading] = useState(!state);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!force && state) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/billing/my-features');
      setState(data);
      _writeCache(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Impossible de charger les modules');
    } finally {
      setLoading(false);
    }
  }, [state]);

  useEffect(() => { load(false); }, [load]);

  // Listen for global invalidation (Superadmin patch, plan change, etc.)
  useEffect(() => {
    const onChange = () => { _clearCache(); load(true); };
    window.addEventListener('ifaap:features-changed', onChange);
    return () => window.removeEventListener('ifaap:features-changed', onChange);
  }, [load]);

  const enabledSet = useMemo(() => new Set((state?.enabled) || []), [state]);
  const has = useCallback((key) => enabledSet.has(key), [enabledSet]);

  return {
    enabled: state?.enabled || [],
    catalog: state?.catalog || [],
    plan: state?.plan_code || null,
    tenantStatus: state?.tenant_status || null,
    has,
    refresh: () => load(true),
    loading,
    error,
  };
}

/**
 * Helper to invalidate the cache from anywhere (e.g., after a Superadmin patch).
 */
export const invalidateFeatures = () => {
  _clearCache();
  try { window.dispatchEvent(new CustomEvent('ifaap:features-changed')); }
  catch { /* noop */ }
};
